import { useQuery } from '@apollo/client';
import { Card, Col, Progress, Row, Statistic, Table, Typography } from 'antd';
import React, { useMemo } from 'react';
import {
  PartnerOnboardingStages,
  PartnerOnboardingStatuses,
  extractPartnerOnboardingStats,
  partnerOnboardingStatsSummaryQuery,
} from './constants';

const { Title, Text } = Typography;

// Status colours are reserved for state and match the dashboard page.
const STATUS_COLOR = {
  IN_PROGRESS: '#2a78d6',
  COMPLETE: '#008300',
  CANCELLED: '#e34948',
};

function StatusTiles({ statusCounts }) {
  const byStatus = useMemo(() => {
    const map = {};
    (statusCounts || []).forEach(({ status, count }) => {
      map[status] = count;
    });
    return map;
  }, [statusCounts]);

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
      {Object.values(PartnerOnboardingStatuses).map((s) => (
        <Col key={s.key} xs={8}>
          <Card
            size="small"
            style={{ borderTop: `3px solid ${STATUS_COLOR[s.key]}` }}
          >
            <Statistic
              title={s.label}
              value={byStatus[s.key] || 0}
              valueStyle={{ color: STATUS_COLOR[s.key] }}
            />
          </Card>
        </Col>
      ))}
    </Row>
  );
}

function StageProgress({ stageTaskCounts }) {
  const rows = useMemo(
    () =>
      (stageTaskCounts || []).map(({ stage, completed, total }) => ({
        stage,
        label: PartnerOnboardingStages[stage]?.label || `Stage ${stage}`,
        completed,
        total,
        pct: total ? Math.round((completed / total) * 100) : 0,
      })),
    [stageTaskCounts],
  );

  if (!rows.length) return null;

  return (
    <>
      <Title level={5} style={{ marginBottom: 12 }}>
        Task Completion by Stage
      </Title>
      <Table
        dataSource={rows}
        rowKey="stage"
        size="small"
        pagination={false}
        style={{ marginBottom: 24 }}
        columns={[
          { title: 'Stage', dataIndex: 'label', width: 160 },
          {
            title: 'Progress',
            dataIndex: 'pct',
            render: (pct) => (
              <Progress percent={pct} size="small" strokeColor="#2a78d6" />
            ),
          },
          {
            title: 'Done',
            key: 'done',
            width: 110,
            render: (_, r) => (
              <Text type="secondary">
                {r.completed} / {r.total}
              </Text>
            ),
          },
        ]}
      />
    </>
  );
}

function PartnersPerQuarter({ quarterFlowCounts }) {
  if (!(quarterFlowCounts || []).length) return null;
  return (
    <>
      <Title level={5} style={{ marginBottom: 12 }}>
        Onboardings Started vs Completed
      </Title>
      <Table
        dataSource={quarterFlowCounts}
        rowKey="quarter"
        size="small"
        pagination={false}
        columns={[
          { title: 'Quarter', dataIndex: 'quarter' },
          { title: 'Started', dataIndex: 'started' },
          { title: 'Completed', dataIndex: 'completed' },
        ]}
      />
    </>
  );
}

function PartnerOnboardingStatsWidget() {
  const { data, loading, error } = useQuery(
    partnerOnboardingStatsSummaryQuery,
    {
      fetchPolicy: 'cache-and-network',
    },
  );
  const stats = extractPartnerOnboardingStats(data);

  if (error) return null;
  if (loading && !stats) return null;

  return (
    <div>
      <Title level={2} style={{ margin: '0 0 16px' }}>
        Partner Onboarding
      </Title>
      <StatusTiles statusCounts={stats?.statusCounts} />
      <StageProgress stageTaskCounts={stats?.stageTaskCounts} />
      <PartnersPerQuarter quarterFlowCounts={stats?.quarterFlowCounts} />
    </div>
  );
}

export default PartnerOnboardingStatsWidget;
